import { authenticatedFetch, ForbiddenError } from "@src/features/auth/services/authenticated-fetch";
import { getSelectedTenantRealm } from "@src/features/tenants";
import { getEnvironment } from "@src/config/environment-store";
import { trackEvent } from "@src/services/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

interface ApiRequestOptions {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  body?: unknown;
  params?: Record<string, string | number | undefined>;
  logMessage?: string;
  timeout?: number; // Timeout in milliseconds (default: 30000 = 30 seconds)
}

export class ApiError extends Error {
  readonly kind = "http" as const;

  constructor(
    public status: number,
    public statusText: string,
    public body?: string,
  ) {
    super(`HTTP error! status: ${status}`);
    this.name = "ApiError";
  }
}

export class ApiTimeoutError extends Error {
  readonly kind = "timeout" as const;

  constructor(
    public url: string,
    public timeoutMs: number,
  ) {
    super(`Request timeout after ${timeoutMs}ms: ${url}`);
    this.name = "ApiTimeoutError";
  }
}

function trackApiTiming(url: string, method: string, durationMs: number, statusCode: number): void {
  try {
    const path = new URL(url).pathname;
    trackEvent(ANALYTICS_EVENTS.API_REQUEST_COMPLETED, {
      method,
      path,
      status_code: statusCode,
      duration_ms: durationMs,
    });
  } catch {
    // Don't let analytics tracking break the app
  }
}

function trackApiError(
  url: string,
  method: string,
  errorType: "http_error" | "timeout" | "network_error",
  statusCode?: number,
  errorMessage?: string,
  durationMs?: number,
): void {
  try {
    const path = new URL(url).pathname;
    trackEvent(ANALYTICS_EVENTS.API_ERROR, {
      error_type: errorType,
      method,
      path,
      status_code: statusCode,
      error_message: errorMessage?.substring(0, 200),
      duration_ms: durationMs,
    });
  } catch {
    // Don't let analytics tracking break the app
  }
}

export async function apiRequest<T>(options: ApiRequestOptions): Promise<T> {
  const { method, path, body, params, logMessage, timeout = 30000 } = options;

  const baseUrl = await getResolvedApiBaseUrl();
  const url = new URL(`${baseUrl}${path}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  const urlString = url.toString();

  if (logMessage) {
    console.log(logMessage, urlString);
  }

  // Create AbortController for timeout
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    console.error(`[API Request] Timeout after ${timeout}ms:`, urlString);
    abortController.abort();
  }, timeout);

  const startTime = Date.now();

  try {
    const fetchOptions: RequestInit = {
      method,
      signal: abortController.signal,
    };

    if (body !== undefined) {
      fetchOptions.headers = { "Content-Type": "application/json" };
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await authenticatedFetch(urlString, fetchOptions);

    const duration = Date.now() - startTime;

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[API Request] Error ${response.status} (${duration}ms):`, errorText);

      trackApiError(urlString, method, "http_error", response.status, errorText, duration);

      // Normalize 403s into a shared ForbiddenError so screens using
      // DataStateView can render a consistent access-denied experience.
      if (response.status === 403) {
        throw new ForbiddenError("Access denied - insufficient permissions");
      }

      throw new ApiError(response.status, response.statusText, errorText);
    }

    trackApiTiming(urlString, method, duration, response.status);

    const data = (await response.json()) as T;
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    const duration = Date.now() - startTime;

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof Error && error.name === "AbortError") {
      console.error(`[API Request] Request aborted (timeout) after ${duration}ms:`, urlString);
      trackApiError(urlString, method, "timeout", undefined, "Request timeout", duration);
      throw new ApiTimeoutError(urlString, timeout);
    }

    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error(`[API Request] Unexpected error for request to ${urlString} after ${duration}ms:`, error);
    trackApiError(urlString, method, "network_error", undefined, errorMessage, duration);
    throw error;
  }
}

export async function getResolvedApiBaseUrl(): Promise<string> {
  const tenantRealm = getSelectedTenantRealm();
  // TODO: Just use the tenant schema TenantInfo schema from a3s-dir instead of redefining in the app.
  //   Right now, this returns 'https://{realmfqdn}' as string. (Not the name nor the api URL for the realm.)
  if (tenantRealm) return tenantRealm;
  // TODO: Force this path to be impossible by construction.
  //   That is, you can't even use this module unless you're able to construct it with a TenantInfo.
  //   For now, we'll try R01. Good luck everyone.
  const env = await getEnvironment();
  console.warn(
    "[DEPRECATED] No tenant was selected. API requests should not be made without resolving a tenant to a realm first.",
  );
  return `https://r01.${env.fqdn}`;
}
